import clsx from 'clsx';
import type { ChangeEvent, ReactNode } from 'react';

/**
 * #2133 デザイン: 入力欄の共通の見た目(ラベルは上、枠線は UI 部品の
 * コントラスト 3:1 を満たす border-strong、フォーカスで primary の枠)。
 */
export const INPUT_CLASS_NAME =
  'block w-full rounded-lg border border-border-strong bg-surface px-3 py-2 text-sm text-text shadow-sm ' +
  'focus:border-primary focus:outline-none focus-visible:outline-none focus:ring-2 focus:ring-primary/40';

interface FieldWrapperProps {
  label: string;
  htmlFor: string;
  required?: boolean;
  /** 2列レイアウトでも横幅いっぱいに置く */
  fullWidth?: boolean;
  children: ReactNode;
}

function FieldWrapper({ label, htmlFor, required, fullWidth, children }: FieldWrapperProps) {
  return (
    <div className={clsx('mb-5', fullWidth && 'md:col-span-2')}>
      <label htmlFor={htmlFor} className="mb-1.5 block text-sm font-bold text-text">
        {label}
        {required && <span className="text-danger"> *</span>}
      </label>
      {children}
    </div>
  );
}

interface TextFieldProps {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: 'text' | 'email' | 'tel' | 'number' | 'date' | 'time';
  required?: boolean;
}

export function TextField({ id, label, value, onChange, type = 'text', required }: TextFieldProps) {
  const handleChange = (event: ChangeEvent<HTMLInputElement>) => onChange(event.target.value);
  return (
    <FieldWrapper label={label} htmlFor={id} required={required}>
      <input
        id={id}
        type={type}
        value={value}
        onChange={handleChange}
        required={required}
        className={INPUT_CLASS_NAME}
      />
    </FieldWrapper>
  );
}

interface TextAreaFieldProps {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
}

export function TextAreaField({ id, label, value, onChange }: TextAreaFieldProps) {
  return (
    <FieldWrapper label={label} htmlFor={id} fullWidth>
      <textarea
        id={id}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        rows={4}
        className={INPUT_CLASS_NAME}
      />
    </FieldWrapper>
  );
}

export interface SelectOption {
  value: string;
  label: string;
}

interface SelectFieldProps {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[];
  placeholder?: string;
}

export function SelectField({ id, label, value, onChange, options, placeholder }: SelectFieldProps) {
  return (
    <FieldWrapper label={label} htmlFor={id}>
      <select
        id={id}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className={INPUT_CLASS_NAME}
      >
        {placeholder !== undefined && <option value="">{placeholder}</option>}
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </FieldWrapper>
  );
}

interface CheckboxFieldProps {
  id: string;
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}

export function CheckboxField({ id, label, checked, onChange }: CheckboxFieldProps) {
  return (
    <div className="mb-5 md:col-span-2">
      <label htmlFor={id} className="inline-flex items-center gap-2 text-sm font-bold text-text">
        <input
          id={id}
          type="checkbox"
          checked={checked}
          onChange={(event) => onChange(event.target.checked)}
          className="h-4 w-4 rounded border-border-strong accent-primary"
        />
        {label}
      </label>
    </div>
  );
}

interface CheckboxGroupFieldProps {
  legend: string;
  options: SelectOption[];
  selected: string[];
  onChange: (selected: string[]) => void;
  emptyMessage?: string;
}

/**
 * 複数選択のチェックボックス群。レッスン編集フォームの繰り返し曜日・生徒
 * 選択で使う(design.md §6 PR#5)。
 */
export function CheckboxGroupField({ legend, options, selected, onChange, emptyMessage }: CheckboxGroupFieldProps) {
  const toggle = (value: string) => {
    onChange(selected.includes(value) ? selected.filter((v) => v !== value) : [...selected, value]);
  };

  return (
    <fieldset className="mb-5 md:col-span-2">
      <legend className="mb-1.5 block text-sm font-bold text-text">{legend}</legend>
      {options.length === 0 && emptyMessage ? (
        <p className="text-sm text-text-muted">{emptyMessage}</p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {options.map((option) => (
            <label
              key={option.value}
              className="inline-flex items-center gap-2 rounded-full border border-border bg-surface px-3 py-1.5 text-sm hover:bg-canvas"
            >
              <input
                type="checkbox"
                checked={selected.includes(option.value)}
                onChange={() => toggle(option.value)}
                className="h-4 w-4 rounded border-border-strong accent-primary"
              />
              {option.label}
            </label>
          ))}
        </div>
      )}
    </fieldset>
  );
}
